import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { Switch } from "@/core/components/ui/switch";
import { Textarea } from "@/core/components/ui/textarea";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { NotificationTemplateDto } from "@/features/notification/api/dto";
import { useCreateNotificationTemplate } from "@/features/notification/api/use-create-notification-template";
import { useNotificationsTranslation } from "@/features/notification/i18n/use-notifications-translation";
import {
  createNotificationTemplateSchema,
  type NotificationTemplateFormValues,
} from "@/features/notification/schemas/notification-template.schema";
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_LANGUAGES,
} from "@/features/shared/api/enums";

const EMPTY_VALUES: NotificationTemplateFormValues = {
  code: "",
  channel: "SMS",
  language: "FR",
  subject: "",
  bodyTemplate: "",
  isActive: true,
};

/**
 * Formulaire création/remplacement d'un modèle de notification
 * (`POST /NotificationTemplates/add`). TanStack Form + schéma zod mémoïsé sur
 * la langue courante (règle projet : jamais d'instance figée). Preview
 * MutationConfirmPreview avant l'appel API.
 */
export function NotificationTemplateFormDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (template: NotificationTemplateDto) => void;
}) {
  const { t } = useTypedTranslation();
  const { t: tNotif } = useNotificationsTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, schéma zod traduit reconstruit à chaque changement de langue (convention wallet.schema.ts).
  const schema = useMemo(() => createNotificationTemplateSchema(), [i18n.language]);

  const create = useCreateNotificationTemplate();
  const pending = create.isPending;
  const [preview, setPreview] = useState<NotificationTemplateDto | null>(null);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const payload: NotificationTemplateDto = {
        code: parsed.data.code,
        channel: parsed.data.channel,
        language: parsed.data.language,
        subject: parsed.data.subject || undefined,
        bodyTemplate: parsed.data.bodyTemplate,
        isActive: parsed.data.isActive,
      };
      setPreview(payload);
    },
  });

  const fieldError =
    (name: keyof NotificationTemplateFormValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{tNotif("notifications.templates.form.title")}</DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="code" validators={{ onChange: fieldError("code") }}>
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="template-code">
                  {tNotif("notifications.templates.form.code")}
                </Label>
                <Input
                  id="template-code"
                  value={field.state.value}
                  placeholder={tNotif("notifications.templates.form.codePlaceholder")}
                  className="font-mono uppercase"
                  onBlur={field.handleBlur}
                  onChange={(e) =>
                    field.handleChange(e.target.value.toUpperCase())
                  }
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="channel" validators={{ onChange: fieldError("channel") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label>{tNotif("notifications.templates.form.channel")}</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) =>
                    field.handleChange(v as NotificationTemplateFormValues["channel"])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NOTIFICATION_CHANNELS.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Field name="language" validators={{ onChange: fieldError("language") }}>
            {(field) => (
              <div className="space-y-1.5">
                <Label>{tNotif("notifications.templates.form.language")}</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(v) =>
                    field.handleChange(v as NotificationTemplateFormValues["language"])
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NOTIFICATION_LANGUAGES.map((v) => (
                      <SelectItem key={v} value={v} className="notranslate">
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          <form.Field name="subject" validators={{ onChange: fieldError("subject") }}>
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="template-subject">
                  {tNotif("notifications.templates.form.subject")}
                </Label>
                <Input
                  id="template-subject"
                  value={field.state.value}
                  placeholder={tNotif(
                    "notifications.templates.form.subjectPlaceholder"
                  )}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field
            name="bodyTemplate"
            validators={{ onChange: fieldError("bodyTemplate") }}
          >
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="template-body">
                  {tNotif("notifications.templates.form.bodyTemplate")}
                </Label>
                <Textarea
                  id="template-body"
                  value={field.state.value}
                  placeholder={tNotif(
                    "notifications.templates.form.bodyTemplatePlaceholder"
                  )}
                  rows={6}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="isActive">
            {(field) => (
              <div className="flex items-center justify-between rounded-md border border-border p-3 sm:col-span-2">
                <Label htmlFor="template-active" className="cursor-pointer">
                  {tNotif("notifications.templates.form.isActive")}
                </Label>
                <Switch
                  id="template-active"
                  checked={field.state.value}
                  onCheckedChange={(v) => field.handleChange(v)}
                />
              </div>
            )}
          </form.Field>

          <DialogFooter className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <MutationConfirmPreview
      open={preview !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) setPreview(null);
      }}
      data={preview}
      title={t("common.mutationPreview.createTitle")}
      description={t("common.mutationPreview.createDescription")}
      t={tNotif}
      pending={pending}
      onConfirm={() => {
        if (!preview) return;
        create.mutate(preview, {
          onSuccess: (data) => {
            notifSilence(tNotif("notifications.templates.feedback.created"));
            if (data && typeof data === "object" && "code" in data) {
              onCreated?.(data as unknown as NotificationTemplateDto);
            }
            setPreview(null);
            onOpenChange(false);
          },
          onError: (error) => notifSilenceError(error.message),
        });
      }}
    />
    </>
  );
}
